/**
 * =============================================================================
 * Mock Data Index
 * =============================================================================
 * Version: 1.0.0
 * Last Modified: 2025-03-02
 * 
 * Central export for all mock data used in local design testing.
 * Switch to real API by replacing imports in components.
 * 
 * =============================================================================
 * USAGE
 * =============================================================================
 * Import mock data:
 * ```typescript
 * import { MOCK_PROPERTIES, searchProperties } from '@/mock';
 * ```
 * 
 * When ready for real API, change to:
 * ```typescript
 * import { PMSClient } from '@/providers';
 * ```
 * 
 * =============================================================================
 * TEST ENVIRONMENT
 * =============================================================================
 * - Container: sunmarbella_test
 * - Database: sunmarbella_db_test
 * - Network: sunmarbella_test_net
 * 
 * =============================================================================
 * CHANGELOG
 * =============================================================================
 * - 1.0.0 (2025-03-02): Initial mock data exports
 * =============================================================================
 */

export {
  MOCK_PROPERTIES,
  MOCK_CITIES,
  AMENITY_ICONS,
  PROPERTY_TYPES,
  getPropertyById,
  searchProperties,
  generateMockCalendar,
  formatPrice,
  type MockProperty,
} from './properties';

// =============================================================================
// MOCK MODE FLAG
// =============================================================================

/**
 * Set to false when ready to connect to real API
 */
export const USE_MOCK_DATA = true;

// =============================================================================
// ADAPTER FOR COMPONENTS
// =============================================================================

/**
 * Transform mock property to component format
 * This allows easy switching between mock and real data
 */
export function toPropertyCardFormat(mock: import('./properties').MockProperty) {
  return {
    id: mock.lodging_id,
    title: mock.lodging_name,
    location: mock.city,
    price: mock.minimum_rate,
    bedrooms: mock.number_of_rooms,
    bathrooms: mock.number_of_bathrooms,
    guests: mock.maximum_capacity,
    images: mock.images.map(img => img.url),
    badges: [mock.category, ...mock.lodging_location.slice(0, 1)],
    rating: mock.rating,
    reviews: mock.review,
    description: mock.headline,
    amenities: mock.lodging_equipment,
  };
}
